import { useEffect, useState } from "react";
import { BarChart3, Brain, Loader2, TrendingUp, TrendingDown, Eye, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader, Spinner, EmptyState, PLATFORM_META } from "@/components/app/widgets";

export default function Analytics() {
  const [platforms, setPlatforms] = useState(null);
  const [campaigns, setCampaigns] = useState(null);
  const [includeDemo, setIncludeDemo] = useState(false);
  const [learning, setLearning] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    const q = includeDemo ? "?include_demo=true" : "";
    setPlatforms(null);
    api.get(`/analytics/platforms${q}`).then((r) => setPlatforms(r.data.items)).catch(() => setPlatforms([]));
    api.get(`/analytics/campaigns${q}`).then((r) => setCampaigns(r.data.items)).catch(() => setCampaigns([]));
    api.get(`/analytics/learning-insights${q}`).then((r) => setLearning(r.data)).catch(() => setLearning(null));
  }, [includeDemo]);

  const generateInsights = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/analytics/learning-insights/generate", { include_demo: includeDemo });
      setLearning(r.data);
      toast.success("Learning insights generated from observed data");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setGenerating(false);
    }
  };

  if (!platforms || !campaigns) return <Spinner label="Loading analytics" />;

  const totalRevenue = platforms.reduce((s, p) => s + p.revenue, 0);
  const maxRevenue = Math.max(1, ...platforms.map((p) => p.revenue));

  return (
    <div data-testid="analytics-page">
      <PageHeader title="Analytics" sub="Real numbers from your own pipeline — nothing is simulated here.">
        <button
          onClick={() => setIncludeDemo(!includeDemo)}
          data-testid="toggle-demo-data"
          className={`rounded-full border px-4 py-2 text-[10px] font-bold uppercase tracking-wider transition-colors ${
            includeDemo ? "border-amber-500/50 bg-amber-500/10 text-amber-400" : "border-white/20 text-zinc-400 hover:border-white/40"
          }`}
        >
          {includeDemo ? "Demo data included" : "Demo data excluded"}
        </button>
      </PageHeader>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="border border-[#27272A] bg-[#0A0A0A] p-6" data-testid="platform-analytics">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Revenue per Platform</h2>
          {platforms.length === 0 ? (
            <p className="mt-4 text-sm text-zinc-500">No platform data yet — it builds as you add prospects, leads and sales.</p>
          ) : (
            <ul className="mt-5 space-y-4">
              {platforms.map((p) => (
                <li key={p.platform} data-testid={`platform-stat-${p.platform}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className={`font-semibold ${PLATFORM_META[p.platform]?.color || "text-zinc-300"}`}>
                      {PLATFORM_META[p.platform]?.label || p.platform}
                    </span>
                    <span className="font-semibold text-[#D4AF37]">${p.revenue.toLocaleString()}</span>
                  </div>
                  <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/10">
                    <div className="h-full rounded-full bg-[#D4AF37]" style={{ width: `${(p.revenue / maxRevenue) * 100}%` }} />
                  </div>
                  <p className="mt-1 text-[11px] text-zinc-500">
                    {p.prospects} prospects · {p.leads} leads · {p.customers} customers
                  </p>
                </li>
              ))}
              <li className="flex items-center justify-between border-t border-white/10 pt-4 text-sm">
                <span className="font-bold uppercase tracking-wider text-zinc-400">Total Attributed Revenue</span>
                <span className="font-serif text-2xl font-semibold">${totalRevenue.toLocaleString()}</span>
              </li>
            </ul>
          )}
        </section>

        <section className="border border-[#27272A] bg-[#0A0A0A] p-6" data-testid="campaign-analytics">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Campaign Performance</h2>
          {campaigns.length === 0 ? (
            <p className="mt-4 text-sm text-zinc-500">No campaigns yet.</p>
          ) : (
            <ul className="mt-5 space-y-4">
              {campaigns.map((c) => (
                <li key={c.campaign_id} data-testid={`campaign-stat-${c.campaign_id}`} className="border border-white/10 p-4">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-white">{c.name}</p>
                    <span className="font-semibold text-[#D4AF37]">${c.revenue.toLocaleString()}</span>
                  </div>
                  <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                    {c.product_name} · {c.objective}
                  </p>
                  <div className="mt-3 grid grid-cols-5 gap-2 text-center text-[11px] text-zinc-500">
                    <span><span className="block text-sm font-semibold text-white">{c.prospects}</span>prospects</span>
                    <span><span className="block text-sm font-semibold text-white">{c.partnerships}</span>partners</span>
                    <span><span className="block text-sm font-semibold text-white">{c.leads}</span>leads</span>
                    <span><span className="block text-sm font-semibold text-white">{c.customers}</span>customers</span>
                    <span><span className="block text-sm font-semibold text-white">{c.conversion_rate}%</span>conv.</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* Learning Insights */}
      <section className="mt-8 border border-[#27272A] bg-[#0A0A0A] p-6" data-testid="learning-insights">
        <div className="flex flex-wrap items-center gap-4">
          <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-300">
            <Brain className="h-4 w-4 text-[#D4AF37]" /> Learning Insights
          </h2>
          {includeDemo && (
            <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-400">
              Includes demo data
            </span>
          )}
          <button
            onClick={generateInsights} disabled={generating}
            data-testid="generate-insights-button"
            className="ml-auto flex items-center gap-2 rounded-full bg-[#D4AF37] px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-black transition-transform duration-300 hover:scale-105 disabled:opacity-50"
          >
            {generating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
            Generate AI Insights
          </button>
        </div>

        {learning?.stats && (
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {[
              ["Outreach sent", learning.stats.totals.outreach_sent],
              ["Responses", learning.stats.totals.responses],
              ["Partnerships", learning.stats.totals.partnerships],
              ["Leads", learning.stats.totals.leads],
              ["Customers", learning.stats.totals.customers],
              ["Avg days → partnership", learning.stats.avg_days_to_partnership ?? "—"],
            ].map(([label, val]) => (
              <div key={label} className="border border-white/10 p-3 text-center">
                <p className="font-serif text-xl font-semibold text-white">{val}</p>
                <p className="mt-0.5 text-[9px] font-bold uppercase tracking-wider text-zinc-500">{label}</p>
              </div>
            ))}
          </div>
        )}

        {learning?.insights ? (
          <div className="mt-6">
            <p className="text-[11px] text-zinc-500">
              Generated {new Date(learning.insights.generated_at).toLocaleString()} · Data sufficiency:{" "}
              <span className="font-semibold text-zinc-300">{learning.insights.insights.data_sufficiency}</span>
              {learning.insights.insights.sufficiency_note ? ` — ${learning.insights.insights.sufficiency_note}` : ""}
            </p>
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <div className="border border-emerald-500/30 bg-emerald-500/5 p-5">
                <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-400">
                  <TrendingUp className="h-4 w-4" /> Do more of this
                </p>
                <ul className="mt-3 list-inside list-disc space-y-2 text-sm leading-relaxed text-zinc-200">
                  {(learning.insights.insights.do_more || []).map((x, i) => <li key={i}>{x}</li>)}
                </ul>
              </div>
              <div className="border border-red-500/30 bg-red-500/5 p-5">
                <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-red-400">
                  <TrendingDown className="h-4 w-4" /> Stop doing this
                </p>
                <ul className="mt-3 list-inside list-disc space-y-2 text-sm leading-relaxed text-zinc-200">
                  {(learning.insights.insights.stop_doing || []).map((x, i) => <li key={i}>{x}</li>)}
                </ul>
              </div>
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {[
                ["Highest-converting prospect types", learning.insights.insights.highest_converting_prospect_types],
                ["Best-performing platforms", learning.insights.insights.best_platforms],
                ["Best value-first offers", learning.insights.insights.best_value_offers],
                ["Best partnership formats", learning.insights.insights.best_partnership_formats],
              ].map(([title, arr]) => (
                <div key={title} className="border border-white/10 p-4">
                  <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-400">{title}</p>
                  <ul className="mt-2 space-y-1.5 text-xs leading-relaxed text-zinc-300">
                    {(arr || []).slice(0, 4).map((x, i) => <li key={i}>· {x}</li>)}
                  </ul>
                </div>
              ))}
            </div>
            {learning.stats.top_partnerships?.length > 0 && (
              <div className="mt-4 border border-white/10 p-5">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">Highest revenue-generating partnerships (observed)</p>
                <ul className="mt-3 divide-y divide-white/5">
                  {learning.stats.top_partnerships.slice(0, 5).map((t) => (
                    <li key={t.prospect_id} className="flex items-center gap-4 py-2.5 text-sm">
                      <span className="min-w-0 flex-1 truncate text-white">{t.name}</span>
                      {t.is_demo_data && <span className="rounded-full border border-amber-500/40 px-2 py-0.5 text-[9px] font-bold uppercase text-amber-400">Demo</span>}
                      <span className="text-xs text-zinc-500">{t.customers} customers</span>
                      <span className="font-semibold text-[#D4AF37]">${t.revenue.toLocaleString()}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <p className="mt-5 flex items-start gap-2 text-sm text-zinc-500">
            <Eye className="mt-0.5 h-4 w-4 shrink-0 text-zinc-600" />
            Observed outcome stats are computed live above. Click "Generate AI Insights" for transparent
            do-more / stop-doing recommendations — AI reasoning is always labelled separately from observed results.
          </p>
        )}
      </section>

      <div className="mt-6 flex items-start gap-3 border border-white/10 bg-[#0A0A0A] p-5">
        <BarChart3 className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" />
        <p className="text-sm leading-relaxed text-zinc-400">
          As real leads and sales flow in, the AI Learning layer will surface "do more of this" /
          "stop doing this" recommendations per platform, partner and campaign. The figures above are
          computed live from your data — never fabricated.
        </p>
      </div>
    </div>
  );
}
